import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

// Learner review form (linked from the after-5-lessons email until a Google review link is added).
export default function LeaveReview() {
  const { user } = useAuth();
  const [google, setGoogle] = useState("");
  const [f, setF] = useState({ rating: 5, quote: "", display_name: "", consent: true });
  const [sent, setSent] = useState(false);
  useEffect(() => { api.get("/reviews/config").then((r) => setGoogle(r.data.google_review_url)).catch(() => {}); }, []);
  useEffect(() => { if (user?.name) setF((x) => ({ ...x, display_name: x.display_name || user.name.split(" ")[0] })); }, [user]);
  const submit = (e) => {
    e.preventDefault();
    api.post("/reviews", f).then(() => setSent(true)).catch((err) => toast.error(err.response?.status === 422 ? "Please write at least a sentence (10+ characters)." : err.response?.data?.detail || "Couldn't send your review — please try again."));
  };
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="mx-auto max-w-xl px-4 py-10 sm:px-8 lg:py-14" data-testid="review-page">
        <h1 className="font-serif text-4xl">How are your lessons going?</h1>
        <p className="mt-3 text-muted-foreground">Thanks for taking a minute — your review helps other people find The JT Hub.</p>
        {google && <a href={google} target="_blank" rel="noreferrer" data-testid="review-google" className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground">Leave a Google review <ExternalLink size={14} /></a>}
        {!user ? (
          <p className="mt-6 rounded-2xl border border-border bg-card p-4 text-sm">Please <Link to="/login" state={{ from: "/review" }} className="font-semibold text-primary underline" data-testid="review-login">log in</Link> to leave a review.</p>
        ) : sent ? (
          <p className="mt-6 rounded-2xl border border-emerald-500 bg-emerald-500/10 p-4 font-semibold" data-testid="review-thanks">Thank you! Jason will read your review soon.</p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4 rounded-2xl border border-border bg-card p-5" data-testid="review-form">
            <div>
              <p className="text-sm font-semibold">Your rating</p>
              <div className="mt-1 flex gap-1">{[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setF({ ...f, rating: n })} aria-label={`${n} star${n > 1 ? "s" : ""}`} data-testid={`review-star-${n}`}>
                  <Star size={28} className={n <= f.rating ? "fill-primary text-primary" : "text-muted-foreground"} />
                </button>
              ))}</div>
            </div>
            <label className="block text-sm font-semibold">Your review
              <textarea required minLength={10} maxLength={800} rows={5} value={f.quote} onChange={(e) => setF({ ...f, quote: e.target.value })} data-testid="review-text"
                className="mt-1 w-full rounded-xl border border-border bg-background p-3 text-sm font-normal focus:border-primary focus:outline-none" placeholder="What have you enjoyed or learned so far?" />
            </label>
            <label className="block text-sm font-semibold">Name to show
              <input required maxLength={60} value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} data-testid="review-name"
                className="mt-1 w-full rounded-full border border-border bg-background px-4 py-2 text-sm font-normal focus:border-primary focus:outline-none" />
            </label>
            <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} data-testid="review-consent" className="mt-1" /> Jason may show my review and name on the website.</label>
            <button type="submit" data-testid="review-submit" className="rounded-full bg-primary px-6 py-2.5 text-sm font-bold text-primary-foreground">Send review</button>
          </form>
        )}
      </main>
    </div>
  );
}
